import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { Database, Json } from "@/types/database"
import { getAboutPageTemplateSections } from "@/components/website-builder/templates/aboutPageTemplate"
import { queuePublicCachePurge } from "@/lib/publicCache"

type Page = Database["public"]["Tables"]["pages"]["Row"]
type PageInsert = Database["public"]["Tables"]["pages"]["Insert"]
type PageUpdate = Database["public"]["Tables"]["pages"]["Update"]

/** Section config for template-based page creation (e.g. custom page templates). */
export interface SectionTemplateConfig {
  section_type: string
  content: Record<string, unknown>
}

/** Extended input for createPage. When page_type is 'about', orgName personalizes the template. */
export interface CreatePageInput extends PageInsert {
  /** When page_type is 'about', used to personalize hero heading etc. Default: "Your Organization" */
  orgName?: string
  /** When page_type is 'custom', optional initial sections (from template). If omitted, page starts empty. */
  initialSections?: SectionTemplateConfig[]
}

export type PageType = "home" | "about" | "donate" | "events" | "blog" | "contact" | "custom"

const PAGE_TYPE_ORDER: PageType[] = ["home", "about", "donate", "events", "blog", "contact", "custom"]

function sortPagesByType(pages: Page[]): Page[] {
  return [...pages].sort((a, b) => {
    const typeA = ((a as Page & { page_type?: string }).page_type ?? (a.slug === "home" ? "home" : "custom")) as PageType
    const typeB = ((b as Page & { page_type?: string }).page_type ?? (b.slug === "home" ? "home" : "custom")) as PageType
    const idxA = PAGE_TYPE_ORDER.indexOf(typeA)
    const idxB = PAGE_TYPE_ORDER.indexOf(typeB)
    if (idxA !== idxB) return idxA - idxB
    return (a.slug ?? "").localeCompare(b.slug ?? "")
  })
}

export interface UsePagesResult {
  pages: Page[]
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

/**
 * Fetch all pages for an org. For multi-page builder.
 */
export function usePages(orgId: string | null): UsePagesResult {
  const [pages, setPages] = useState<Page[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId) {
      setPages([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: e } = await supabase
      .from("pages")
      .select("*")
      .eq("org_id", orgId)
      .order("slug", { ascending: true })
    if (e) {
      setError(e.message)
      setPages([])
    } else {
      setPages(sortPagesByType((data ?? []) as Page[]))
    }
    setLoading(false)
  }, [orgId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { pages, loading, error, refresh }
}

export interface UsePageResult {
  page: Page | null
  loading: boolean
  error: string | null
  refresh: () => Promise<void>
}

/**
 * Fetch a single page by slug or id. For editing and public rendering.
 */
export function usePage(
  orgId: string | null,
  slugOrId: string | null
): UsePageResult {
  const [page, setPage] = useState<Page | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!orgId || !slugOrId) {
      setPage(null)
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      slugOrId
    )
    const query = isUuid
      ? supabase.from("pages").select("*").eq("id", slugOrId).eq("org_id", orgId)
      : supabase.from("pages").select("*").eq("slug", slugOrId).eq("org_id", orgId)

    const { data, error: e } = await query.maybeSingle()
    if (e) {
      setError(e.message)
      setPage(null)
    } else {
      setPage((data as Page) ?? null)
    }
    setLoading(false)
  }, [orgId, slugOrId])

  useEffect(() => {
    refresh()
  }, [refresh])

  return { page, loading, error, refresh }
}

export interface UseCreatePageResult {
  createPage: (input: CreatePageInput) => Promise<Page | null>
  loading: boolean
  error: string | null
}

/**
 * Create a new page. Returns the created page or null on error.
 * When page_type is 'about', applies the About page template with default sections.
 */
export function useCreatePage(orgId: string | null): UseCreatePageResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createPage = useCallback(
    async (input: CreatePageInput): Promise<Page | null> => {
      if (!orgId) return null
      setLoading(true)
      setError(null)

      const { orgName, initialSections, ...pageInsert } = input
      const { data, error: e } = await supabase
        .from("pages")
        .insert({ ...pageInsert, org_id: orgId })
        .select()
        .single()
      setLoading(false)

      if (e) {
        const msg = e.code === "23505" && e.message?.includes("slug")
          ? "A page with this URL path already exists. Choose a different slug."
          : e.message
        setError(msg)
        return null
      }

      const page = data as Page
      queuePublicCachePurge(orgId, { type: "page-create", pageSlug: page.slug })
      const pageType = input.page_type ?? (page as Page & { page_type?: string }).page_type

      if (pageType === "about") {
        const orgDisplayName = orgName?.trim() || "Your Organization"
        const sections = getAboutPageTemplateSections(orgDisplayName)
        for (let i = 0; i < sections.length; i++) {
          const { section_type, content } = sections[i]
          const { error: sectionErr } = await supabase.from("page_sections").insert({
            page_id: page.id,
            org_id: orgId,
            section_type,
            order_index: i,
            content: content as Json,
            is_visible: true,
          })
          if (sectionErr) {
            setError(sectionErr.message)
            return null
          }
        }
      }

      if (pageType === "custom" && initialSections && initialSections.length > 0) {
        for (let i = 0; i < initialSections.length; i++) {
          const { section_type, content } = initialSections[i]
          const { error: sectionErr } = await supabase.from("page_sections").insert({
            page_id: page.id,
            org_id: orgId,
            section_type,
            order_index: i,
            content: content as Json,
            is_visible: true,
          })
          if (sectionErr) {
            setError(sectionErr.message)
            return null
          }
        }
      }

      return page
    },
    [orgId]
  )

  return { createPage, loading, error }
}

export interface UseUpdatePageResult {
  updatePage: (id: string, updates: PageUpdate) => Promise<boolean>
  loading: boolean
  error: string | null
}

/**
 * Update a page by id. Returns true on success.
 */
export function useUpdatePage(orgId: string | null): UseUpdatePageResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const updatePage = useCallback(
    async (id: string, updates: PageUpdate): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      // A slug rename must purge the old URL too — capture it before the write.
      let previousSlug: string | undefined
      if (updates.slug !== undefined) {
        const { data: current } = await supabase
          .from("pages")
          .select("slug")
          .eq("id", id)
          .eq("org_id", orgId)
          .maybeSingle()
        previousSlug = (current as { slug: string } | null)?.slug
      }
      const { data, error: e } = await supabase
        .from("pages")
        .update(updates)
        .eq("id", id)
        .eq("org_id", orgId)
        .select("slug")
        .maybeSingle()
      setLoading(false)
      if (e) {
        setError(e.message)
        return false
      }
      const slug = (data as { slug: string } | null)?.slug
      if (slug) {
        queuePublicCachePurge(orgId, { type: "page-settings-save", pageSlug: slug, previousSlug })
      }
      return true
    },
    [orgId]
  )

  return { updatePage, loading, error }
}

export interface UseDeletePageResult {
  deletePage: (id: string) => Promise<boolean>
  loading: boolean
  error: string | null
}

/**
 * Delete a page by id. Returns true on success.
 * Guards: cannot delete the home page (slug='home').
 */
export function useDeletePage(orgId: string | null): UseDeletePageResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const deletePage = useCallback(
    async (id: string): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { data: page, error: fetchErr } = await supabase
        .from("pages")
        .select("slug")
        .eq("id", id)
        .eq("org_id", orgId)
        .maybeSingle()
      if (fetchErr || !page) {
        setError(fetchErr?.message ?? "Page not found")
        setLoading(false)
        return false
      }
      if ((page as { slug: string }).slug === "home") {
        setError("Cannot delete the home page")
        setLoading(false)
        return false
      }
      const { error: e } = await supabase
        .from("pages")
        .delete()
        .eq("id", id)
        .eq("org_id", orgId)
      setLoading(false)
      if (e) {
        setError(e.message)
        return false
      }
      queuePublicCachePurge(orgId, {
        type: "page-delete",
        pageSlug: (page as { slug: string }).slug,
      })
      return true
    },
    [orgId]
  )

  return { deletePage, loading, error }
}

export interface UsePublishPageResult {
  publishPage: (id: string, publish: boolean) => Promise<boolean>
  loading: boolean
  error: string | null
}

/**
 * Publish or unpublish a page. Returns true on success.
 */
export function usePublishPage(orgId: string | null): UsePublishPageResult {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const publishPage = useCallback(
    async (id: string, publish: boolean): Promise<boolean> => {
      if (!orgId) return false
      setLoading(true)
      setError(null)
      const { data, error: e } = await supabase
        .from("pages")
        .update({ is_published: publish })
        .eq("id", id)
        .eq("org_id", orgId)
        .select("slug")
        .maybeSingle()
      setLoading(false)
      if (e) {
        setError(e.message)
        return false
      }
      const slug = (data as { slug: string } | null)?.slug
      if (slug) {
        queuePublicCachePurge(orgId, {
          type: publish ? "page-publish" : "page-unpublish",
          pageSlug: slug,
        })
      }
      return true
    },
    [orgId]
  )

  return { publishPage, loading, error }
}
